import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { useInspections } from '@/hooks/useInspections';
import { useInspectorProjects } from '@/hooks/useInspectorProjects';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Calendar, FileText, Image as ImageIcon, ArrowLeft } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

export default function InspectorHistory() {
  const navigate = useNavigate();
  const { user } = usePortalAuth();
  const { data: projects, isLoading: projectsLoading } = useInspectorProjects(user?.id);
  const { inspections, isLoading: inspectionsLoading } = useInspections();
  
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Filter inspections for this inspector
  const myInspections = inspections?.filter(i => i.inspector_id === user?.id) || [];

  // Apply filters
  const filteredInspections = myInspections.filter(inspection => {
    if (statusFilter !== 'all' && inspection.status !== statusFilter) return false;
    if (projectFilter !== 'all' && inspection.project_id !== projectFilter) return false;
    if (dateFrom && new Date(inspection.date) < new Date(dateFrom)) return false;
    if (dateTo && new Date(inspection.date) > new Date(dateTo)) return false;
    return true;
  });

  const getStatusBadge = (status: string) => {
    const variants: Record<string, string> = {
      concept: 'bg-gray-100 text-gray-800',
      ingediend: 'bg-blue-100 text-blue-800',
      verwerkt: 'bg-green-100 text-green-800',
    };
    return variants[status] || variants.concept;
  };

  const getProjectName = (projectId: string) => {
    const project = projects?.find(p => p.id === projectId);
    return project ? `${project.name} (${project.project_number || 'N/A'})` : 'Onbekend project';
  };

  if (projectsLoading || inspectionsLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/inspector')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Inspectie Geschiedenis</h1>
          <p className="text-muted-foreground">Overzicht van al je inspecties</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Status</label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Alle statussen" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle statussen</SelectItem>
                <SelectItem value="concept">Concept</SelectItem>
                <SelectItem value="ingediend">Ingediend</SelectItem>
                <SelectItem value="verwerkt">Verwerkt</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Project</label>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Alle projecten" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle projecten</SelectItem>
                {projects?.map(project => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Van datum</label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Tot datum</label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>

        {(statusFilter !== 'all' || projectFilter !== 'all' || dateFrom || dateTo) && (
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={() => {
              setStatusFilter('all');
              setProjectFilter('all');
              setDateFrom('');
              setDateTo('');
            }}
          >
            Filters wissen
          </Button>
        )}
      </Card>

      {/* Results */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {filteredInspections.length} {filteredInspections.length === 1 ? 'inspectie' : 'inspecties'} gevonden
          </p>
        </div>

        {filteredInspections.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Geen inspecties gevonden</h3>
            <p className="text-muted-foreground">
              {statusFilter !== 'all' || projectFilter !== 'all' || dateFrom || dateTo
                ? 'Probeer andere filters'
                : 'Je hebt nog geen inspecties uitgevoerd'}
            </p>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredInspections.map((inspection) => (
              <Card
                key={inspection.id}
                className="p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/inspector/inspection/${inspection.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold">{getProjectName(inspection.project_id!)}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(inspection.status)}`}>
                        {inspection.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <FileText className="h-4 w-4" />
                        <span>{inspection.type}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>{format(new Date(inspection.date), 'PPP', { locale: nl })}</span>
                      </div>
                      {inspection.photo_urls && Array.isArray(inspection.photo_urls) && (
                        <div className="flex items-center gap-1">
                          <ImageIcon className="h-4 w-4" />
                          <span>{inspection.photo_urls.length} foto's</span>
                        </div>
                      )}
                    </div>
                    {inspection.notes && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                        {inspection.notes}
                      </p>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
